import { useEffect, useState } from 'react';
import {
  Box,
  Container,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from '@mui/material';
import Page from '../../components/Page';
import Typography from '@mui/material/Typography';
import { useAppDispatch, useAppSelector } from '../../store';
import { findDashboardReport } from '../../store/dashboardReport/api';
import { TIME_PERIOD } from '../../constants';
import { TimePeriod } from '../../enum';
import { OverviewTotalCustomers } from './app/overview-customers';
import { FaGift } from 'react-icons/fa';
import { VscGraphLine } from 'react-icons/vsc';
import { MdAttachMoney } from 'react-icons/md';
import { OverviewSales } from './app/overview-sales';
import { OverviewExpense } from './app/overview-expense';
import Loader from '../../components/Loader';
import DashboardOrderTable from './app/orderTable';
import { findSellingCakeReport } from '../../store/sellingCakeReport/api';
import Cakes from './app/cakes';
import Toppings from './app/toppings';
import Addons from './app/addOns';
import Shapes from './app/shapes';
import { getSettings } from '../../store/settings/api';
import { setAssetUrl } from '../../store/active/activeSlice';
import { getItem, setItem } from '../../utils/localStorage';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers';
import { searchDateFormat } from '../../utils/searchDateFormat';

const Index = () => {
  const dispatch = useAppDispatch();
  const [timeDuration, setTimeDuration] = useState<string>(TimePeriod.today);
  const [startDate, setStartDate] = useState<Date | null>(new Date());
  const [endDate, setEndDate] = useState<Date | null>(new Date());
  const { firstName, lastName, userRole } = useAppSelector(
    (state) => state.auth.user
  );
  const { data, isLoading } = useAppSelector((state) => state.dashboardReport);
  const { data: sellingCakeData, isLoading: sellingCakeIsLoading } =
    useAppSelector((state) => state.sellingCakeReport);
  const assetUrl = getItem('asset-url');

  useEffect(() => {
    if (userRole === 'admin' || userRole === 'finance') {
      if (timeDuration !== TimePeriod.customRange) {
        dispatch(
          findDashboardReport({
            timePeriod: timeDuration,
          })
        );
      } else {
        if (startDate && endDate) {
          dispatch(
            findDashboardReport({
              timePeriod: timeDuration,
              ...(endDate &&
                startDate && { endDate: searchDateFormat(endDate) }),
              ...(endDate &&
                startDate && { startDate: searchDateFormat(startDate) }),
            })
          );
        }
      }
    }
  }, [dispatch, timeDuration, startDate, endDate]);

  useEffect(() => {
    if (userRole === 'admin' || userRole === 'finance') {
      if (timeDuration !== TimePeriod.customRange) {
        dispatch(
          findSellingCakeReport({
            timePeriod: timeDuration,
          })
        );
      } else {
        if (startDate && endDate) {
          dispatch(
            findSellingCakeReport({
              timePeriod: timeDuration,
              ...(endDate &&
                startDate && { endDate: searchDateFormat(endDate) }),
              ...(endDate &&
                startDate && { startDate: searchDateFormat(startDate) }),
            })
          );
        }
      }
    }
  }, [dispatch, timeDuration, startDate, endDate]);

  useEffect(() => {
    const fetchById = async () => {
      const result = await dispatch(
        getSettings({
          id: 'b78d6064-0072-4249-bb26-9f8f97ad07f6',
        })
      ).unwrap();
      if (result?.value) {
        setItem('asset-url', result?.value);
        dispatch(setAssetUrl({ assetUrl: result?.value }));
      }
    };
    if (!assetUrl) {
      fetchById();
    }
  }, []);

  return (
    <Page title='Dashboard'>
      {(userRole === 'admin' || userRole === 'finance') && (
        <Container maxWidth='xl'>
          {isLoading ? (
            <div
              style={{
                height: '76vh',
                display: 'flex',
                alignItems: 'center',
              }}
            >
              <Loader />
            </div>
          ) : (
            <>
              <Grid
                container
                spacing={2}
                sx={{ pt: 3 }}
              >
                <Grid
                  item
                  xs={12}
                  sm={6}
                >
                  <Box sx={{ pb: 1 }}>
                    <Typography variant='h4'>
                      Hi, {firstName} {lastName}. Welcome back
                    </Typography>
                  </Box>
                </Grid>
                <Grid
                  item
                  xs={0}
                  sm={3}
                ></Grid>

                <Grid
                  item
                  xs={12}
                  sm={3}
                >
                  <FormControl fullWidth>
                    <InputLabel>{'Time Duration'}</InputLabel>
                    <Select
                      name='timeDuration'
                      labelId='timeDuration'
                      id='timeDuration'
                      value={timeDuration}
                      label={'Time Duration'}
                      onChange={(e) => setTimeDuration(e.target.value)}
                      MenuProps={{
                        style: {
                          maxHeight: 400,
                        },
                      }}
                    >
                      {TIME_PERIOD?.map((time) => (
                        <MenuItem
                          key={time.value}
                          value={time.value}
                        >
                          {time.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
              {timeDuration === TimePeriod.customRange && (
                <Grid
                  container
                  spacing={2}
                  sx={{ py: 2.5, px: 3 }}
                >
                  <Grid
                    item
                    xs={12}
                    sm={4}
                  ></Grid>
                  <Grid
                    item
                    xs={12}
                    sm={4}
                  >
                    <LocalizationProvider dateAdapter={AdapterDateFns}>
                      <DatePicker
                        label={'Start Date'}
                        value={startDate ?? ''}
                        maxDate={endDate ?? undefined}
                        onChange={(value: Date | null) => {
                          setStartDate(value);
                        }}
                        inputFormat='dd/MM/yyyy'
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            fullWidth
                            name='startDate'
                            error={false}
                          />
                        )}
                      />
                    </LocalizationProvider>
                  </Grid>
                  <Grid
                    item
                    xs={12}
                    sm={4}
                  >
                    <LocalizationProvider dateAdapter={AdapterDateFns}>
                      <DatePicker
                        label={'End Date'}
                        value={endDate ?? ''}
                        minDate={startDate ?? undefined}
                        onChange={(newValue: Date | null) => {
                          setEndDate(newValue);
                        }}
                        inputFormat='dd/MM/yyyy'
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            fullWidth
                            name='endDate'
                            error={false}
                          />
                        )}
                      />
                    </LocalizationProvider>
                  </Grid>
                </Grid>
              )}

              <Grid
                container
                spacing={2}
                sx={{ pt: 3 }}
              >
                <Grid
                  item
                  xs={12}
                  sm={4}
                >
                  <OverviewTotalCustomers
                    difference={16}
                    title={'Average Order Value'}
                    sx={{ height: '100%' }}
                    value={data?.averageOrderValue}
                    icon={<MdAttachMoney />}
                  />
                </Grid>
                <Grid
                  item
                  xs={12}
                  sm={4}
                >
                  <OverviewTotalCustomers
                    difference={16}
                    title={'Average Daily Orders'}
                    sx={{ height: '100%' }}
                    value={data?.averageDailyOrders}
                    icon={<FaGift />}
                  />
                </Grid>
                <Grid
                  item
                  xs={12}
                  sm={4}
                >
                  <OverviewTotalCustomers
                    difference={16}
                    title={'Average Daily Sales'}
                    sx={{ height: '100%' }}
                    value={data?.averageDailySales}
                    icon={<VscGraphLine />}
                  />
                </Grid>
              </Grid>

              <Grid
                container
                spacing={2}
                sx={{ pt: 3 }}
              >
                <Grid
                  item
                  xs={12}
                  sm={8}
                >
                  {data?.totalSales && (
                    <OverviewSales
                      // data={[
                      //   { date: '9/07/2024', value: 1 },
                      //   { date: '10/07/2024', value: 5 },
                      // ]}
                      data={data?.totalSales}
                      // sx={{ height: '100%' }}
                    />
                  )}
                </Grid>
                <Grid
                  item
                  xs={12}
                  sm={4}
                  md={4}
                  lg={4}
                >
                  {data?.totalOrders && (
                    <OverviewExpense
                      // data={[
                      //   { date: '9/07/2024', value: 1 },
                      //   { date: '10/07/2024', value: 5 },
                      // ]}
                      data={data?.totalOrders}
                      sx={{ height: '100%' }}
                    />
                  )}
                </Grid>
              </Grid>

              {sellingCakeIsLoading ? (
                <Loader />
              ) : (
                <Grid
                  container
                  spacing={2}
                  sx={{ pt: 3 }}
                >
                  <Grid
                    item
                    xs={12}
                    sm={6}
                    md={6}
                  >
                    <Cakes
                      topSellingCakes={sellingCakeData?.topSellingCakes}
                      lessSellingCakes={sellingCakeData?.lessSellingCakes}
                    />
                  </Grid>
                  <Grid
                    item
                    xs={12}
                    sm={6}
                    md={6}
                  >
                    <Shapes
                      topSellingShapes={sellingCakeData?.topSellingShapes}
                      lessSellingShapes={sellingCakeData?.lessSellingShapes}
                    />
                  </Grid>
                  <Grid
                    item
                    xs={12}
                    sm={6}
                    md={6}
                  >
                    <Toppings
                      topSellingToppings={sellingCakeData?.topSellingToppings}
                      lessSellingToppings={sellingCakeData?.lessSellingToppings}
                    />
                  </Grid>
                  <Grid
                    item
                    xs={12}
                    sm={6}
                    md={6}
                  >
                    <Addons
                      topSellingAddon={sellingCakeData?.topSellingAddon}
                      lessSellingAddon={sellingCakeData?.lessSellingAddon}
                    />
                  </Grid>
                </Grid>
              )}

              <Grid
                container
                spacing={2}
                sx={{ pt: 3 }}
              >
                <Grid
                  item
                  xs={12}
                  lg={12}
                >
                  <DashboardOrderTable />
                </Grid>
              </Grid>
            </>
          )}
        </Container>
      )}
    </Page>
  );
};

export default Index;
